import { lazy } from 'react'
import { observer } from 'mobx-react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import LoginPage from 'pages/LoginPage'
import PlayLayoutRoute from 'Router/play-layout-route'
import routes from 'routes'
import { AuthenticateParams } from 'configs/constants/enums'
import { EFeatureFlags } from 'constants/featureFlags'
import { useStores } from 'hooks/useStores'
import useFeatureFlagValue from 'hooks/useFeatureFlagValue'
import PlanStationDetail from 'components/Pages/PlansPage/components/PlanStationDetail'
import DashboardPage from 'pages/DashboardPage'
import SearchPage from 'pages/SearchPage'
import AuthedShell from './authed-shell'
import EmailVerifyGuard from './email-verify-guard'

const SignUpPage = lazy(() => import('pages/SignUpPage'))
const CreateAccountPage = lazy(() => import('pages/CreateAccountPage'))
const VerifyEmailPage = lazy(() => import('pages/VerifyEmailPage'))
const AddYouthAthleteProfilePage = lazy(() => import('pages/AddYouthAthleteProfilePage'))
const GetStartedPage = lazy(() => import('pages/GetStartedPage'))
const CreateOrganizationPage = lazy(() => import('pages/CreateOrganizationPage'))
const ForgotPasswordPage = lazy(() => import('pages/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('pages/ResetPasswordPage'))
const OrgPickerPage = lazy(() => import('pages/OrgPickerPage'))
const SelectProfilePage = lazy(() => import('pages/SelectProfilePage'))
const GoogleSsoRoleSelectPage = lazy(() => import('pages/GoogleSsoRoleSelectPage'))
const LinkGooglePage = lazy(() => import('pages/LinkGooglePage'))
const PlayProfilePage = lazy(() => import('pages/PlayProfilePage'))
const AccountSettingsPage = lazy(() => import('pages/AccountSettingsPage'))
const EditProfilePage = lazy(() => import('pages/EditProfilePage'))
const EditMetricsPage = lazy(() => import('pages/EditMetricsPage'))

const ArchivePage = lazy(() => import('pages/ArchivePage'))
const CollectionDetailPage = lazy(() => import('pages/CollectionDetailPage'))
const CollectionsPage = lazy(() => import('pages/CollectionsPage'))
const GroupPage = lazy(() => import('pages/GroupPage'))
const GroupDetailPage = lazy(() => import('pages/GroupUserPage'))
const IconBuilderPage = lazy(() => import('pages/IconBuilderPage'))
const InboxPage = lazy(() => import('pages/InboxPage'))
const NotificationPage = lazy(() => import('pages/NotificationPage'))
const OrganizationDetailPage = lazy(() => import('pages/OrganizationDetailPage'))
const OrganizationReportPage = lazy(() => import('pages/OrganizationReportPage'))
const ProcessDetailPage = lazy(() => import('pages/ProcessDetailPage'))
const ProcessPage = lazy(() => import('pages/ProcessPage'))
const ContentItemPage = lazy(() => import('pages/ContentItemPage'))
const ContentItemDetailPage = lazy(() => import('pages/ContentItemDetailPage'))
const PlansPage = lazy(() => import('components/Pages/PlansPage'))
const SchedulePage = lazy(() => import('components/Pages/SchedulePage'))
const ScheduleAssignPage = lazy(() => import('components/Pages/SchedulePage/components/ScheduleAssignPage'))
const WorkPlanDetailPage = lazy(() => import('pages/WorkPlanDetailPage'))
const TrainingPlanDetailPage = lazy(() => import('pages/TrainingPlanDetailPage'))
const WorkPlanPreviewPage = lazy(() => import('pages/WorkPlanPreviewPage'))
const TagPage = lazy(() => import('pages/TagPage'))
const UserDetailPage = lazy(() => import('pages/UserDetailPage'))
const OrganizationPage = lazy(() => import('pages/OrganizationPage'))
const NotFoundPage = lazy(() => import('pages/NotFoundPage'))
const MetricPage = lazy(() => import('pages/MetricPage'))
const BillingPage = lazy(() => import('pages/BillingPage'))
const TeamsPage = lazy(() => import('pages/TeamsPage'))
const TeamFormPage = lazy(() => import('pages/TeamFormPage'))
const TeamDetailPage = lazy(() => import('pages/TeamDetailPage'))
const TrainingAssignmentPage = lazy(() => import('pages/TrainingAssignmentPage'))
const TrainingAssignmentDetailPage = lazy(() => import('pages/TrainingAssignmentDetailPage'))
const PlayerAssignmentListPage = lazy(() => import('pages/PlayerAssignmentListPage'))
const PlayerPracticePlanPage = lazy(() => import('pages/PlayerPracticePlanPage'))
const PlayerWeekDetailPage = lazy(() => import('pages/PlayerWeekDetailPage'))
const PlayerDrillDetailPage = lazy(() => import('pages/PlayerDrillDetailPage'))
const PlayerProfilePage = lazy(() => import('pages/PlayerProfilePage'))

const RequireAuth = ({ children }: { children: JSX.Element }) => {
  const accessToken = localStorage.getItem(AuthenticateParams.ACCESS_TOKEN) || ''
  if (!accessToken) {
    return <Navigate to={routes.login.value} replace />
  }
  return children
}

/**
 * Entry-point guard for `/` and unknown URLs.
 * - Not authenticated → LoginPage.
 * - Authenticated → /dashboard (the merged cross-team landing page).
 * Renders nothing while auth bootstrap is still resolving so we don't flash
 * LoginPage at a logged-in user before MobX has loaded their profile.
 */
const RootRedirect = observer(() => {
  const { authStore } = useStores()
  const accessToken = localStorage.getItem(AuthenticateParams.ACCESS_TOKEN) || ''
  if (!accessToken) {
    return <LoginPage />
  }
  if (!authStore.userDetail) {
    return null
  }
  return <Navigate to={routes.dashboard.value} replace />
})

// Onboarding still hands off through /play-dashboard and carries the welcome
// variant in the query string, so the redirect has to keep the search intact —
// a bare <Navigate to="/dashboard"> drops it and the welcome modal never shows.
const LegacyPlayDashboardRedirect = () => {
  const { search } = useLocation()
  return <Navigate to={`${routes.dashboard.value}${search}`} replace />
}

const PublicRoutes = () => {
  const { value: isWorkPlansEnabled } = useFeatureFlagValue(EFeatureFlags.WORK_PLANS, true)

  return (
    <Routes>
      <Route path="" element={<RootRedirect />} />
      <Route path={routes.login.value} element={<LoginPage />} />
      <Route path={routes.signUp.value} element={<SignUpPage />} />
      <Route path={routes.createAccount.value} element={<CreateAccountPage />} />
      <Route path={routes.verifyEmail.value} element={<VerifyEmailPage />} />
      <Route path={routes.youthAthleteProfile.value} element={<AddYouthAthleteProfilePage />} />
      {/* Coach / Program Admin onboarding, entered straight after email verification. */}
      <Route path={routes.getStarted.value} element={<GetStartedPage />} />
      <Route path={routes.createOrganization.value} element={<CreateOrganizationPage />} />
      <Route path={routes.forgotPassword.value} element={<ForgotPasswordPage />} />
      <Route path={routes.resetPassword.value} element={<ResetPasswordPage />} />
      <Route path={routes.googleSsoRoleSelect.value} element={<GoogleSsoRoleSelectPage />} />
      <Route path={routes.linkGoogle.value} element={<LinkGooglePage />} />
      <Route
        path={routes.selectOrganization.value}
        element={
          <RequireAuth>
            <EmailVerifyGuard>
              <OrgPickerPage />
            </EmailVerifyGuard>
          </RequireAuth>
        }
      />
      <Route
        path={routes.selectProfile.value}
        element={
          <RequireAuth>
            <EmailVerifyGuard>
              <SelectProfilePage />
            </EmailVerifyGuard>
          </RequireAuth>
        }
      />
      {/* Authed routes: lifted from legacy SubDomainRoutes during cross-team flatten.
          AuthedShell handles email-verify gate, org-picker bounce, and SW navigation. */}
      <Route
        element={
          <RequireAuth>
            <AuthedShell />
          </RequireAuth>
        }
      >
        <Route path={routes.dashboard.value} element={<DashboardPage />} />
        {/* Common Library hidden in the cross-team migration — same treatment
            as Appearance + SSO. Page source preserved; route 404s. */}
        <Route path={routes.commonLibrary.value} element={<NotFoundPage />} />
        <Route path={routes.collections.value} element={<CollectionsPage />} />
        <Route path="/collections/:collectionId" element={<CollectionDetailPage />} />
        <Route path={routes.processes.value} element={<ProcessPage />} />
        <Route path={routes.search.value} element={<SearchPage />} />
        <Route path={routes.archive.value} element={<ArchivePage />} />
        {isWorkPlansEnabled && <Route path={routes.plans.value} element={<PlansPage />} />}
        {isWorkPlansEnabled && <Route path="/plans/:id" element={<WorkPlanDetailPage />} />}
        {isWorkPlansEnabled && <Route path="/plans/training/:id" element={<TrainingPlanDetailPage />} />}
        {isWorkPlansEnabled && <Route path="/station/:id" element={<PlanStationDetail />} />}
        {isWorkPlansEnabled && <Route path="/training-assignments/:id" element={<TrainingAssignmentDetailPage />} />}
        {isWorkPlansEnabled && (
          <Route path={routes.plans.trainingAssignments.value} element={<TrainingAssignmentPage />} />
        )}
        <Route path={routes.schedule.value} element={<SchedulePage />} />
        {isWorkPlansEnabled && <Route path="/schedule/:eventId" element={<ScheduleAssignPage />} />}
        <Route path="/drills/:processId" element={<ProcessDetailPage />} />
        <Route path={routes.contentItems.value} element={<ContentItemPage />} />
        <Route path="/content-items/:contentItemId" element={<ContentItemDetailPage />} />
        <Route path={routes.notifications.value} element={<NotificationPage />} />
        <Route path={routes.messages.value} element={<InboxPage />} />
        <Route path={routes.organization.value} element={<OrganizationPage />} />
        {/* Team Members moved into the Organization page's Org Membership tab.
            Kept as a redirect so existing links and bookmarks still land. */}
        <Route path={routes.users.value} element={<Navigate to={routes.organization.value} replace />} />
        <Route path="/users/:userId" element={<UserDetailPage />} />
        <Route path={routes.groups.value} element={<GroupPage />} />
        {/* Document Types route removed: the feature is deprecated and hidden from the UI. */}
        <Route path={routes.setting.iconBuilder.value} element={<IconBuilderPage />} />
        <Route path={routes.setting.tag.value} element={<TagPage />} />
        {/* Cross-team migration: appearance + SSO are hidden product surface.
            Direct URL hits render the 404 page rather than the legacy editors. */}
        <Route path={routes.setting.appearance.value} element={<NotFoundPage />} />
        <Route path={routes.setting.sso.value} element={<NotFoundPage />} />
        <Route path={routes.setting.metric.value} element={<MetricPage />} />
        <Route path={routes.setting.billing.value} element={<BillingPage />} />
        <Route path="/groups/:groupId" element={<GroupDetailPage />} />
        <Route path={routes.report.organization.value} element={<OrganizationReportPage />} />
        <Route path={`${routes.report.organization.value}/:id`} element={<OrganizationDetailPage />} />
        <Route path={routes.teams.value} element={<TeamsPage />} />
        {/* /teams/new must precede /teams/:teamId so the literal "new" is not
            consumed as a dynamic teamId segment. */}
        <Route path={routes.teams.new.value} element={<TeamFormPage />} />
        <Route path={routes.teams.detail.value(':teamId')} element={<TeamDetailPage />} />
        {isWorkPlansEnabled && <Route path={routes.playerAssignments.value} element={<PlayerAssignmentListPage />} />}
        {isWorkPlansEnabled && <Route path="/player/assignments/:assignmentId" element={<PlayerPracticePlanPage />} />}
        {isWorkPlansEnabled && (
          <Route path="/player/assignments/:assignmentId/week/:weekId" element={<PlayerWeekDetailPage />} />
        )}
        {isWorkPlansEnabled && (
          <Route path="/player/assignments/:assignmentId/station/:stationId" element={<PlayerDrillDetailPage />} />
        )}
        <Route path={routes.playerProfile.value} element={<PlayerProfilePage />} />
        <Route path="/player-profile/:userId" element={<PlayerProfilePage />} />
      </Route>
      {/* Legacy /play-dashboard redirects to the merged cross-team /dashboard. */}
      <Route path="/play-dashboard" element={<LegacyPlayDashboardRedirect />} />
      {/* Play profile keeps its dedicated layout for now (Phase 06 follow-up). */}
      <Route
        element={
          <RequireAuth>
            <PlayLayoutRoute />
          </RequireAuth>
        }
      >
        <Route path="/play-profile" element={<PlayProfilePage />} />
        <Route path="/play-profile/:profileId" element={<PlayProfilePage />} />
        <Route path="/account-settings" element={<AccountSettingsPage />} />
        <Route path="/edit-profile" element={<EditProfilePage />} />
        <Route path="/edit-profile/metrics" element={<EditMetricsPage />} />
      </Route>
      {isWorkPlansEnabled && (
        <Route
          path={routes.plans.previewId.value(':id')}
          element={
            <RequireAuth>
              <WorkPlanPreviewPage />
            </RequireAuth>
          }
        />
      )}
      <Route path="*" element={<RootRedirect />} />
    </Routes>
  )
}

export default observer(PublicRoutes)
